// Tour de contrôle (docs/TOWER.md) : des retours aux PR approuvées. Équipe
// seulement ; l'approbation est en plus réservée aux approbateurs du
// registre, vérifiés par le serveur. Types repris de Faast.
import type { RealtimeEvent } from './types'

export type TowerState =
  | 'queued' | 'running' | 'verifying' | 'reviewing' | 'building' | 'preview'
  | 'awaiting_approval' | 'approved' | 'deployed' | 'failed' | 'stopped'

export type TowerSource = 'client' | 'team'
export type TowerSeverity = 'low' | 'medium' | 'high' | 'critical'
export type TowerTicketType = 'fix' | 'feature' | 'chore'
export type TowerVerdict = 'ready' | 'unclear' | 'injection'

export interface TowerTicketRow {
  id: string
  code: string
  state: TowerState
  stage: number
  title: string
  summary: string
  project: string
  source: TowerSource
  company?: string
  severity: TowerSeverity
  type: TowerTicketType
  created_at: string
  updated_at: string
  spent_usd: number
  budget_usd: number
  pr_number?: number
  preview_url?: string
  error?: string
  feedback_id: string
  feedback_status: string
  /** App du retour d'origine (lien vers le tableau). */
  app?: string
  /** Posé quand l'équipe a archivé le ticket. */
  archived_at?: string
  /** Seul un ticket terminé s'archive : décidé par l'API. */
  can_archive: boolean
  /** Qui a fait naître le ticket (docs/TOWER-MULTI.md). */
  origin: TowerOrigin
  /** Codes des tickets amont de la même demande. */
  depends_on: string[]
  /** Ce que le ticket attend avant de partir (« attend T-1050 (caast_api) en cours »). */
  waiting?: string
  /** Retiré de sa demande par l'équipe : arrêté, plus attendu, ne se relance pas. */
  removed?: boolean
}

/** triage : créé par le tri ; team : ajouté par l'équipe ; agent : besoin amont déclaré par l'agent d'un autre ticket. */
export type TowerOrigin = 'triage' | 'team' | 'agent'

/** Un ticket de la même demande, vu depuis l'un d'eux. */
export interface TowerDemandMember {
  id: string
  code: string
  project: string
  state: TowerState
  origin: TowerOrigin
  depends_on: string[]
  waiting?: string
  removed?: boolean
  /** L'équipe peut le retirer de la demande (décidé par l'API). */
  can_remove: boolean
}

/** « Ajouter un ticket à cette demande » : avant (amont, attendu) ou après (aval). */
export interface TowerAddTicket {
  project: string
  position: 'before' | 'after'
  summary: string
  criteria: string[]
  type?: TowerTicketType
  severity?: TowerSeverity
}

export interface TowerOverview {
  to_triage: number
  active_agents: number
  slots: number
  awaiting_approval: number
  delivered_week: number
  median_seconds: number
  spent_today_usd: number
  daily_cap_usd: number
  global_paused: boolean
  /** Nombre de tickets archivés (hors de `tickets`). */
  archived: number
  tickets: TowerTicketRow[]
}

export interface TowerCheck {
  key: 'sha' | 'ci' | 'review' | 'preview' | 'upstream'
  label: string
  ok: boolean
  detail?: string
}

export interface TowerOriginal {
  title: string
  body: string
  redacted: Record<string, number>
  untrusted: boolean
}

export interface TowerJobRow {
  id: string
  stage: string
  model?: string
  state: string
  started_at?: string
  finished_at?: string
  cost_usd: number
  last_line: string
  ticket?: string
  project?: string
  budget_usd?: number
}

export interface TowerEventRow {
  at: string
  step: string
  status: 'ok' | 'info' | 'error' | 'anomaly'
  message: string
  attrs?: Record<string, unknown>
}

export interface TowerTicketDetail extends TowerTicketRow {
  criteria: string[]
  confidence: number
  autonomy: string
  branch: string
  head_sha?: string
  pr_url?: string
  ci_tests?: number
  ci_ok: boolean
  review_ok: boolean
  review_summary?: string
  checks: TowerCheck[]
  card_expires_at?: string
  can_approve: boolean
  original: TowerOriginal
  jobs: TowerJobRow[]
  events: TowerEventRow[]
  /** Remarques de l'équipe, lues par l'agent à chaque passe. */
  notes: TowerNote[]
  repo: string
  default_branch: string
  /** Les tickets de la demande, dans l'ordre ; vide pour une demande à un seul ticket. */
  demand: TowerDemandMember[]
}

export interface TowerNoteMedia {
  id: string
  width: number
  height: number
  size: number
}

export interface TowerNote {
  id: number
  at: string
  author?: string
  body: string
  media: TowerNoteMedia[]
}

export interface TowerProposal {
  project: string
  type: TowerTicketType
  summary: string
  criteria: string[]
  severity: TowerSeverity
  confidence: number
  pii_detected: boolean
  injection_suspected: boolean
  injection_reason: string
  /** Autres projets que la demande exige vraiment (rare). */
  also?: TowerProposalPart[]
}

/** Un projet de plus dans la demande, avant (amont) ou après (aval) le projet principal. */
export interface TowerProposalPart {
  project: string
  order: 'before' | 'after'
  summary: string
  criteria: string[]
  reason: string
}

export interface TowerTriageCard {
  feedback_id: string
  status: 'pending' | 'proposed' | 'asked' | 'error'
  verdict?: TowerVerdict
  proposal?: TowerProposal
  reasons: string[]
  original: TowerOriginal
  source: TowerSource
  company?: string
  created_at: string
  error?: string
  cost_usd: number
  can_create: boolean
}

export interface TowerDispatcher {
  runner_id: string
  node: string
  cluster: string
  image_digest: string
  version: string
  last_seen_at: string
  connected: boolean
}

export interface TowerCapacity {
  project: string
  active: number
  queued: number
  max: number
  paused: boolean
  enabled: boolean
}

export interface TowerAgents {
  dispatchers: TowerDispatcher[]
  spent_today_usd: number
  daily_cap_usd: number
  spent_by_project: Record<string, number>
  capacity: TowerCapacity[]
  running: TowerJobRow[]
  global_paused: boolean
}

export interface TowerProject {
  key: string
  label: string
  enabled: boolean
  apps: string[]
  repo: string
  default_branch: string
  protected_branches: string[]
  runtime: string
  preview: { kind: string; api_url?: string; host_suffix?: string }
  budget: { max_usd: number; max_minutes: number }
  max_parallel: number
  max_level: string
  approver_names: string[]
}

export interface TowerProposalEdit {
  project: string
  type: TowerTicketType
  summary: string
  criteria: string[]
  severity: TowerSeverity
  /** Liste complète des projets supplémentaires ; absente, celle du tri reste. */
  also?: TowerProposalPart[]
}

export interface TowerControlsUpdate {
  global_paused?: boolean
  project?: string
  project_paused?: boolean
  daily_cap_usd?: number
}

/** Un retour vu de la tour : projet visé, transmission, ticket. */
export interface TowerItem {
  feedback_id: string
  app: string
  target_project: string
  forwarded_at?: string | null
  projects: TowerProject[]
  ticket: { id: string; code: string; state: TowerState; pr_url?: string } | null
  /** Tous les tickets de la demande (le premier est aussi dans ticket). */
  tickets?: { id: string; code: string; state: TowerState; project: string; pr_url?: string; removed?: boolean }[]
  triage: { status: TowerTriageCard['status'] | 'ticket_created' | 'refused'; verdict?: string } | null
}

export interface TowerRunner {
  id: string
  name: string
  created_at: string
  last_seen_at?: string | null
  revoked_at?: string | null
}

/** Les événements temps réel de la tour. */
export const TOWER_EVENTS = ['tower.ticket', 'tower.job', 'tower.triage', 'tower.controls'] as const

export function isTowerEvent(e: RealtimeEvent): boolean {
  return (TOWER_EVENTS as readonly string[]).includes(e.type)
}

interface Requester {
  request<T>(method: string, path: string, opts?: { body?: unknown; query?: Record<string, string | number | boolean | undefined>; form?: FormData; blob?: boolean }): Promise<T>
}

const enc = encodeURIComponent

/** L'API de la tour, branchée sur un client. */
export function towerApi(c: Requester) {
  return {
    overview: () => c.request<TowerOverview>('GET', '/api/tower/overview'),
    counts: () => c.request<{ to_triage: number; awaiting_approval: number; total: number }>('GET', '/api/tower/counts'),
    me: () => c.request<{ is_approver: boolean }>('GET', '/api/tower/me'),
    ticket: (idOrCode: string) => c.request<TowerTicketDetail>('GET', `/api/tower/tickets/${enc(idOrCode)}`),
    triages: () => c.request<{ data: TowerTriageCard[] }>('GET', '/api/tower/triages').then((r) => r.data),
    agents: () => c.request<TowerAgents>('GET', '/api/tower/agents'),
    projects: () => c.request<{ data: TowerProject[] }>('GET', '/api/tower/projects').then((r) => r.data),
    createTicket: (feedbackId: string) => c.request<{ id: string; code: string }>('POST', `/api/tower/triages/${enc(feedbackId)}/ticket`),
    editTriage: (feedbackId: string, edit: TowerProposalEdit) => c.request<void>('PATCH', `/api/tower/triages/${enc(feedbackId)}`, { body: edit }),
    refuseTriage: (feedbackId: string, report: boolean) => c.request<void>('POST', `/api/tower/triages/${enc(feedbackId)}/refuse`, { body: { report } }),
    askApprover: (feedbackId: string) => c.request<void>('POST', `/api/tower/triages/${enc(feedbackId)}/ask`),
    approve: (id: string, sha: string) =>
      c.request<{ state: TowerState; pr_url: string; pr_number: number }>('POST', `/api/tower/tickets/${enc(id)}/approve`, { body: { sha } }),
    sendBack: (id: string, note: string) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/send-back`, { body: { note } }),
    /** fresh : « Repartir de zéro » (clone frais) ; sinon l'agent reprend sa branche. */
    retry: (id: string, fresh = false) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/retry`, { body: { fresh } }),
    /** Remarque de l'équipe : texte et/ou captures (images). */
    addNote: (id: string, note: string, files: Blob[] = []) => {
      const form = new FormData()
      form.append('note', note)
      for (const f of files) form.append('files', f, (f as File).name || 'capture.png')
      return c.request<void>('POST', `/api/tower/tickets/${enc(id)}/notes`, { form })
    },
    /** Ajoute un ticket à la demande du ticket id, avant ou après lui. */
    addToDemand: (id: string, t: TowerAddTicket) => c.request<{ id: string; code: string }>('POST', `/api/tower/tickets/${enc(id)}/demand`, { body: t }),
    /** Retire le ticket de sa demande : il s'arrête et n'est plus attendu. */
    removeFromDemand: (id: string) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/remove-from-demand`),
    /** Remplace les tickets amont (codes T-<n> de la même demande) ; [] : plus d'attente. */
    setDependencies: (id: string, dependsOn: string[]) => c.request<void>('PUT', `/api/tower/tickets/${enc(id)}/dependencies`, { body: { depends_on: dependsOn } }),
    noteMedia: (id: string, mediaId: string) => c.request<Blob>('GET', `/api/tower/tickets/${enc(id)}/media/${enc(mediaId)}`, { blob: true }),
    stop: (id: string) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/stop`),
    archive: (id: string) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/archive`),
    unarchive: (id: string) => c.request<void>('POST', `/api/tower/tickets/${enc(id)}/unarchive`),
    archiveMany: (ids: string[]) => c.request<{ archived: number }>('POST', '/api/tower/archive', { body: { ids } }),
    archived: () => c.request<{ data: TowerTicketRow[] }>('GET', '/api/tower/archive').then((r) => r.data),
    controls: (u: TowerControlsUpdate) => c.request<void>('PATCH', '/api/tower/controls', { body: u }),
    item: (feedbackId: string) => c.request<TowerItem>('GET', `/api/tower/items/${enc(feedbackId)}`),
    setTarget: (feedbackId: string, project: string) => c.request<void>('PATCH', `/api/tower/items/${enc(feedbackId)}/target`, { body: { project } }),
    forward: (feedbackId: string) => c.request<void>('POST', `/api/tower/items/${enc(feedbackId)}/forward`),
    runners: {
      list: () => c.request<{ data: TowerRunner[] }>('GET', '/api/tower/runners').then((r) => r.data),
      create: (name: string) => c.request<{ runner: TowerRunner; token: string }>('POST', '/api/tower/runners', { body: { name } }),
      revoke: (id: string) => c.request<void>('DELETE', `/api/tower/runners/${enc(id)}`),
    },
  }
}

export type TowerApi = ReturnType<typeof towerApi>
